import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  IArnoMappingRule,
  useArnoMappingRules,
  useResetArnoMappingRule,
  useSetArnoMappingRuleDecision,
} from '../../hooks/reactQuerys/ArnoAdminQueries';
import { EVIDENCE_LABEL } from './fieldEvidence';
import {
  familySummaries,
  indexSentence,
  isConfirmable,
  missingForConfirmation,
  patternParameters,
  ruleImpactSentence,
  RULE_STATUS_LABEL,
} from './mappingRules';
import { useAlert } from '../../context/AlertContext';

/**
 * Familienregeln für nummerierte Legacy-Felder (#1245).
 *
 * `BST1_BL1_FLURSTÜCK` bis `BST10_BL12_FLURSTÜCK` sind 120 Felder und eine
 * einzige fachliche Entscheidung. Entschieden wird deshalb die Schablone — und
 * genau deswegen steht hier vor der Schaltfläche, was ihr noch fehlt.
 */

export const FamilyRulePanel: React.FC = () => {
  const { showAlert } = useAlert();
  const { mappingRules, isFetchingMappingRules } = useArnoMappingRules();
  const { setMappingRuleDecision, isSettingMappingRuleDecision } =
    useSetArnoMappingRuleDecision();
  const { resetMappingRule, isResettingMappingRule } =
    useResetArnoMappingRule();
  const [selected, setSelected] = useState<IArnoMappingRule | null>(null);

  const rows = useMemo(() => mappingRules?.rows ?? [], [mappingRules]);
  const families = useMemo(() => familySummaries(rows), [rows]);
  const blocked = rows.reduce(
    (total, rule) => total + (rule.impact?.blockedIfs ?? 0),
    0
  );

  const decide = async (
    rule: IArnoMappingRule,
    status: 'CONFIRMED' | 'IGNORED'
  ) => {
    try {
      await setMappingRuleDecision({
        idLegacyMappingRule: rule.idLegacyMappingRule,
        status,
      });
      showAlert({
        severity: 'success',
        text:
          status === 'CONFIRMED'
            ? `${rule.sourcePattern} ist bestätigt.`
            : `${rule.sourcePattern} bleibt bewusst ohne Ziel.`,
      });
      setSelected(null);
    } catch (error) {
      showAlert({
        severity: 'error',
        text:
          error instanceof Error
            ? `Nicht möglich: ${error.message}`
            : 'Nicht möglich.',
      });
    }
  };

  const undo = async (rule: IArnoMappingRule) => {
    await resetMappingRule(rule.idLegacyMappingRule);
    setSelected(null);
  };

  return (
    <Box>
      <Alert severity='info' sx={{ mb: 2 }}>
        <AlertTitle>
          {rows.length} Regeln über {families.length} Familien · {blocked}{' '}
          blockierte Bedingungen
        </AlertTitle>
        <Typography variant='body2'>
          Eine Regel ordnet eine ganze Zahlenfamilie auf einmal zu. Bestätigt
          wird sie nur, wenn die Bedeutung belegt ist, ein DiNo-Zielfeld
          existiert und für jede Zahl ausgesprochen ist, womit Altbestand und
          DiNo zählen.
        </Typography>
      </Alert>

      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Familie</TableCell>
            <TableCell align='right'>Regeln</TableCell>
            <TableCell align='right'>Legacy-Felder</TableCell>
            <TableCell align='right'>Blockierte Bedingungen</TableCell>
            <TableCell>Beweislage</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {families.map(family => (
            <TableRow key={family.family}>
              <TableCell>{family.family}</TableCell>
              <TableCell align='right'>
                {family.confirmed > 0
                  ? `${family.confirmed} / ${family.rules}`
                  : family.rules}
              </TableCell>
              <TableCell align='right'>{family.fields}</TableCell>
              <TableCell align='right'>{family.blockedIfs}</TableCell>
              <TableCell>{EVIDENCE_LABEL[family.evidence]}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Divider sx={{ my: 2 }} />

      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Schablone</TableCell>
            <TableCell>DiNo-Ziel</TableCell>
            <TableCell align='right'>Felder</TableCell>
            <TableCell align='right'>Blockiert</TableCell>
            <TableCell
              align='right'
              title='Eine Bedingung wird nur übersetzt, wenn auch jede äußere Bedingung über ihr übersetzt wird.'
            >
              davon erreichbar
            </TableCell>
            <TableCell align='right'>Konflikte</TableCell>
            <TableCell>Stand</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(rule => (
            <TableRow
              key={rule.idLegacyMappingRule}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => setSelected(rule)}
            >
              <TableCell sx={{ fontFamily: 'monospace' }}>
                {rule.sourcePattern}
              </TableCell>
              <TableCell sx={{ fontFamily: 'monospace' }}>
                {rule.targetPattern ?? '–'}
              </TableCell>
              <TableCell align='right'>{rule.impact?.fields ?? 0}</TableCell>
              <TableCell align='right'>
                {rule.impact?.blockedIfs ?? 0}
              </TableCell>
              <TableCell
                align='right'
                sx={{
                  color:
                    (rule.impact?.blockedReachableIfs ?? 0)
                      ? 'text.primary'
                      : 'text.disabled',
                }}
              >
                {rule.impact?.blockedReachableIfs ?? 0}
              </TableCell>
              <TableCell align='right'>
                {rule.impact?.conflictCount ?? 0}
              </TableCell>
              <TableCell>
                <Chip
                  size='small'
                  color={rule.status === 'CONFIRMED' ? 'success' : 'default'}
                  label={RULE_STATUS_LABEL[rule.status] ?? rule.status}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {isFetchingMappingRules && (
        <Typography variant='caption' color='text.secondary'>
          Regeln werden gerechnet …
        </Typography>
      )}

      <Drawer
        anchor='right'
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        PaperProps={{ sx: { width: { xs: '100%', md: 640 }, p: 3 } }}
      >
        {selected && (
          <Box>
            <Typography variant='h6' sx={{ fontFamily: 'monospace' }}>
              {selected.sourcePattern}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {selected.family} · {EVIDENCE_LABEL[selected.evidenceStatus]} ·{' '}
              {RULE_STATUS_LABEL[selected.status] ?? selected.status}
            </Typography>

            <Typography variant='body2' sx={{ mt: 2 }}>
              {selected.semantics}
            </Typography>

            <Typography
              variant='overline'
              color='text.secondary'
              sx={{ mt: 2, display: 'block' }}
            >
              Indexregel
            </Typography>
            {patternParameters(selected.sourcePattern).map(name => (
              <Typography
                key={name}
                variant='body2'
                sx={{ fontFamily: 'monospace' }}
              >
                {indexSentence(selected, name)}
              </Typography>
            ))}

            <Typography
              variant='overline'
              color='text.secondary'
              sx={{ mt: 2, display: 'block' }}
            >
              Wirkung
            </Typography>
            <Typography variant='body2'>
              {ruleImpactSentence(selected)}
            </Typography>

            {(selected.impact?.examples ?? []).length > 0 && (
              <>
                <Typography
                  variant='overline'
                  color='text.secondary'
                  sx={{ mt: 2, display: 'block' }}
                >
                  Beispiele
                </Typography>
                {(selected.impact?.examples ?? []).slice(0, 6).map(example => (
                  <Typography
                    key={example.sourceKey}
                    variant='caption'
                    component='div'
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {example.sourceKey} → {example.selector ?? '– kein Ziel –'}
                  </Typography>
                ))}
              </>
            )}

            {(selected.impact?.conflictCount ?? 0) > 0 && (
              <Alert severity='warning' sx={{ mt: 2 }}>
                <AlertTitle>
                  {selected.impact?.conflictCount} Konflikte
                </AlertTitle>
                {(selected.impact?.conflicts ?? [])
                  .slice(0, 5)
                  .map(conflict => (
                    <Typography
                      key={conflict.sourceKey}
                      variant='caption'
                      component='div'
                    >
                      {conflict.sourceKey}:{' '}
                      {conflict.kind === 'EXPLICIT_MAPPING'
                        ? `eigene Zuordnung auf ${conflict.detail}`
                        : `auch getroffen von ${conflict.detail}`}
                    </Typography>
                  ))}
              </Alert>
            )}

            {selected.blocker && (
              <Alert severity='warning' sx={{ mt: 2 }}>
                <AlertTitle>Was heute fehlt</AlertTitle>
                {selected.blocker}
              </Alert>
            )}

            {missingForConfirmation(selected).length > 0 ? (
              <Alert severity='info' sx={{ mt: 2 }}>
                <AlertTitle>Noch nicht entscheidbar</AlertTitle>
                {missingForConfirmation(selected).map(line => (
                  <Typography key={line} variant='body2'>
                    • {line}
                  </Typography>
                ))}
              </Alert>
            ) : (
              <Stack direction='row' spacing={1} sx={{ mt: 2 }}>
                <Button
                  variant='contained'
                  disabled={
                    !isConfirmable(selected) || isSettingMappingRuleDecision
                  }
                  onClick={() => decide(selected, 'CONFIRMED')}
                >
                  Regel bestätigen
                </Button>
                <Button
                  variant='outlined'
                  disabled={isSettingMappingRuleDecision}
                  onClick={() => decide(selected, 'IGNORED')}
                >
                  Bewusst ohne Ziel
                </Button>
              </Stack>
            )}

            {selected.isHuman && (
              <Button
                size='small'
                sx={{ mt: 1 }}
                disabled={isResettingMappingRule}
                onClick={() => undo(selected)}
              >
                Entscheidung zurücknehmen
              </Button>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
};
